/**
 * O IDIOMA DO TURNO — o valor que a tela manda ao motor em toda requisição de
 * chat (bloco 5, spec `axiomgo-brain/specs/2026-08-20-idioma-ponta-a-ponta.md`).
 *
 * POR QUE ISTO EXISTE. Até 20/08/2026 o seletor de idioma da barra lateral
 * trocava o i18next e gravava `axg-locale` no navegador, e PARAVA AÍ. Nenhuma
 * requisição carregava o valor, e o motor só sabia ler `User.language` — que
 * nasce `pt_BR` por default do esquema e que nenhuma rota escrevia. Resultado
 * medido: moldura em inglês, agente em português.
 *
 * UMA LEITURA, UM LUGAR. As duas portas do motor (`chat-stream.ts` no SSE e
 * `connection-manager.ts` no REST) leem daqui, para não existir a chance de uma
 * mandar o idioma e a outra não — que é como o produto ficaria bilíngue por
 * canal.
 *
 * A ESCOLHA DE CANAL, e o porquê: o valor viaja como CAMPO DO CORPO (`locale`),
 * não como cabeçalho HTTP. O corpo já carrega todo o resto do que o turno
 * declara (`onboardingAction`, `commandSource`, `npsAction`), e cabeçalho novo
 * exigiria entrar na lista de CORS do motor em três lugares — que é exatamente
 * como este produto já matou um botão em silêncio antes (ver o JSDoc de
 * `CORS_ALLOWED_HEADERS` em `axiomgo-engine/src/index.ts`).
 */
import i18n from './i18n/config'
import { USER_LOCALE_KEY } from './locale-detection'

/**
 * O idioma que o usuário está LENDO agora, na forma BCP-47 que o i18next
 * resolveu (`pt-BR` | `en-US` | `es-419`).
 *
 * Defensivo de propósito: um i18next ainda não inicializado (teste isolado,
 * primeiro tique do bootstrap) devolve `undefined`, e aqui isso vira `null` —
 * o campo simplesmente não entra no corpo, e o motor cai em `User.language`
 * como sempre fez. Nunca lança: uma leitura de idioma não pode derrubar o envio
 * de uma mensagem.
 */
export function currentTurnLocale(): string | null {
  try {
    const lng = i18n.language
    return typeof lng === 'string' && lng.length > 0 ? lng : null
  } catch {
    return null
  }
}

/**
 * STO-1401/1479 — a pessoa ESCOLHEU o idioma neste aparelho, ou o valor que
 * `currentTurnLocale()` devolve é ambiente (palpite do navegador ou default
 * de produto/tenant)? O motor só carimba `User.languageSource = 'selected'`
 * (a única origem que vence o default do tenant — `resolveTurnLocale`,
 * `orchestrator/turn-locale.ts` do motor) quando este sinal chega `true` no
 * corpo do turno (`localeExplicit`). Deriva de `USER_LOCALE_KEY`
 * (`axg-locale-user`) — a MESMA chave que a RAIZ 10 só escreve por seleção
 * explícita (`locale-detection.ts`), nunca pelo palpite do navegador nem
 * pela etapa 2 (default do tenant, `i18n/config.ts`).
 */
export function currentTurnLocaleExplicit(): boolean {
  try {
    return localStorage.getItem(USER_LOCALE_KEY) !== null
  } catch {
    return false
  }
}
